import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, ShieldX, Home, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';

/**
 * User persona types for strict RBAC
 */
export type UserPersona = 'buyer' | 'dealer_admin' | 'salesperson' | 'admin' | null;

/**
 * Loading spinner component for auth checks
 */
function AuthLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

/**
 * 403 Forbidden component for unauthorized deep-link access attempts
 * Shows forbidden page instead of redirect to prevent information disclosure
 */
function ForbiddenAccess({ attemptedPath }: { attemptedPath: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <ShieldX className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl">403 Forbidden</CardTitle>
          <CardDescription className="text-base">
            You don't have permission to access this page
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            The resource at <code className="px-1 py-0.5 bg-muted rounded text-xs">{attemptedPath}</code> requires different access privileges.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              className="flex-1"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
            <Button asChild className="flex-1">
              <Link to="/">
                <Home className="h-4 w-4 mr-2" />
                Home
              </Link>
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground text-center pt-4">
            If you believe this is an error, please contact support.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Hook to detect user's persona based on their database records
 * OPTIMIZED: Uses parallel queries and leverages AuthContext's isDealer check
 * to avoid redundant database calls
 */
export function useUserPersona() {
  const { user, isAdmin, isDealer, isLoading: authLoading, isRoleLoading } = useAuth();
  const [persona, setPersona] = useState<UserPersona>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDealerAdmin, setIsDealerAdmin] = useState(false);
  const [isSalesperson, setIsSalesperson] = useState(false);
  const [isBuyer, setIsBuyer] = useState(false);

  useEffect(() => {
    // Wait for BOTH auth AND role loading to complete
    if (authLoading || isRoleLoading) return;
    
    if (!user) {
      setPersona(null);
      setIsDealerAdmin(false);
      setIsSalesperson(false);
      setIsBuyer(false);
      setIsLoading(false);
      return;
    }

    const detectPersona = async () => {
      try {
        // Check admin first (highest privilege) - already loaded by AuthContext
        if (isAdmin) {
          setPersona('admin');
          setIsLoading(false);
          return;
        }

        // isDealer from AuthContext already tells us if they're dealer_admin OR salesperson
        // We just need to determine which one with a SINGLE parallel query
        if (isDealer) {
          // Run both checks in parallel to determine dealer_admin vs salesperson
          const [dealerResult, salesResult] = await Promise.all([
            supabase
              .from('dealer_accounts')
              .select('id')
              .eq('user_id', user.id)
              .eq('status', 'approved')
              .maybeSingle(),
            supabase
              .from('dealer_sales_team')
              .select('id')
              .eq('user_id', user.id)
              .eq('is_active', true)
              .maybeSingle()
          ]);

          if (dealerResult.data) {
            setPersona('dealer_admin');
            setIsDealerAdmin(true);
            setIsLoading(false);
            return;
          }

          if (salesResult.data) {
            setPersona('salesperson');
            setIsSalesperson(true);
            setIsLoading(false);
            return;
          }
        }

        // If not admin or dealer, check buyer profile
        const { data: buyerProfile } = await supabase
          .from('buyer_profiles')
          .select('id')
          .eq('id', user.id)
          .maybeSingle();

        if (buyerProfile) {
          setPersona('buyer');
          setIsBuyer(true);
          setIsLoading(false);
          return;
        }

        // Default: user exists but no specific persona yet
        setPersona(null);
        setIsLoading(false);
      } catch (error) {
        console.error('Error detecting user persona:', error);
        setPersona(null);
        setIsLoading(false);
      }
    };

    detectPersona();
  }, [user, isAdmin, isDealer, authLoading, isRoleLoading]);

  return { persona, isLoading: authLoading || isRoleLoading || isLoading, isDealerAdmin, isSalesperson, isBuyer };
}

interface RouteGuardProps {
  children: ReactNode;
  fallbackPath?: string;
}

/**
 * Base protected route - requires authentication
 */
export function ProtectedRoute({ children, fallbackPath = '/auth/buyer' }: RouteGuardProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <AuthLoader />;
  }

  if (!user) {
    // Encode the current path for redirect after login
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${fallbackPath}?returnTo=${returnTo}`} replace />;
  }

  return <>{children}</>;
}

/**
 * Buyer-only route guard
 * Prevents dealers, salespeople, and admins from accessing buyer pages
 * Shows 403 Forbidden for unauthorized deep-link attempts
 * OPTIMIZED: Uses AuthContext's isRoleLoading to wait for role checks
 */
export function BuyerRoute({ children }: RouteGuardProps) {
  const { user, isLoading: authLoading, isRoleLoading, isAdmin, isDealer } = useAuth();
  const { isLoading: personaLoading, isSalesperson } = useUserPersona();
  const location = useLocation();

  // Wait for auth AND role checks to complete before deciding
  // This prevents false 403s during initial load
  if (authLoading || isRoleLoading || personaLoading) {
    return <AuthLoader />;
  }

  // Not logged in - redirect to buyer auth
  if (!user) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/buyer?returnTo=${returnTo}`} replace />;
  }

  // Admin trying to access buyer page - 403 Forbidden
  if (isAdmin) {
    return <ForbiddenAccess attemptedPath={location.pathname} />;
  }

  // Dealer admin trying to access buyer page - 403 Forbidden
  if (isDealer) {
    return <ForbiddenAccess attemptedPath={location.pathname} />;
  }

  // Salesperson trying to access buyer page - 403 Forbidden
  if (isSalesperson) {
    return <ForbiddenAccess attemptedPath={location.pathname} />;
  }

  // User is a buyer or hasn't completed onboarding yet
  return <>{children}</>;
}

/**
 * Dealer admin route guard
 * Only allows dealer account owners
 * Shows 403 Forbidden for unauthorized access attempts
 */
export function DealerAdminRoute({ children }: RouteGuardProps) {
  const { user, isLoading, isRoleLoading, isAdmin, isDealer } = useAuth();
  const location = useLocation();

  // Wait for auth AND role checks to fully load - prevents 403 flash on hard refresh
  if (isLoading || isRoleLoading) {
    return <AuthLoader />;
  }

  // Not logged in - redirect to dealer auth with next parameter
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/dealer/login?next=${next}`} replace />;
  }

  // Admins can access dealer pages for support purposes
  if (isAdmin) {
    return <>{children}</>;
  }

  // Must be a dealer admin (isDealer from AuthContext)
  if (!isDealer) {
    return <ForbiddenAccess attemptedPath={location.pathname} />;
  }

  return <>{children}</>;
}

/**
 * Salesperson route guard
 * Allows salespeople (and dealer admins for their team oversight)
 * Shows 403 Forbidden for unauthorized access attempts
 * OPTIMIZED: Uses AuthContext's isRoleLoading
 */
export function SalespersonRoute({ children }: RouteGuardProps) {
  const { user, isLoading: authLoading, isRoleLoading, isAdmin, isDealer } = useAuth();
  const { isLoading: personaLoading, isSalesperson } = useUserPersona();
  const location = useLocation();

  // Wait for auth, role, and persona to load
  if (authLoading || isRoleLoading || personaLoading) {
    return <AuthLoader />;
  }

  // Not logged in - redirect to dealer auth with next parameter
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/dealer/login?next=${next}`} replace />;
  }

  // Admins can access for support
  if (isAdmin) {
    return <>{children}</>;
  }

  // Dealer admins trying to access salesperson-specific page - 403 Forbidden
  if (isDealer) {
    return <ForbiddenAccess attemptedPath={location.pathname} />;
  }

  // Must be a salesperson
  if (!isSalesperson) {
    return <ForbiddenAccess attemptedPath={location.pathname} />;
  }

  return <>{children}</>;
}

/**
 * Dealer route guard (allows both dealer admins and salespeople)
 * Shows 403 Forbidden for buyers attempting access
 * OPTIMIZED: Uses AuthContext's isRoleLoading
 */
export function DealerRoute({ children }: RouteGuardProps) {
  const { user, isLoading: authLoading, isRoleLoading, isAdmin, isDealer } = useAuth();
  const { isLoading: personaLoading, isSalesperson } = useUserPersona();
  const location = useLocation();

  // Wait for auth, role, and persona to load
  if (authLoading || isRoleLoading || personaLoading) {
    return <AuthLoader />;
  }

  // Not logged in - redirect to dealer auth with next parameter
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/dealer/login?next=${next}`} replace />;
  }

  // Admins can access for support
  if (isAdmin) {
    return <>{children}</>;
  }

  // Must be dealer admin (isDealer) or salesperson
  if (!isDealer && !isSalesperson) {
    return <ForbiddenAccess attemptedPath={location.pathname} />;
  }

  return <>{children}</>;
}

/**
 * Admin route guard
 * Only allows users with admin role
 * Shows 403 Forbidden for non-admin users attempting access
 */
export function AdminRoute({ children }: RouteGuardProps) {
  const { user, isAdmin, isLoading, isRoleLoading } = useAuth();
  const location = useLocation();

  // Show loader while auth OR role check is being determined
  // This prevents the flash of 403 on hard refresh
  if (isLoading || isRoleLoading) {
    return <AuthLoader />;
  }

  // Not logged in - redirect to admin login with next parameter
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/admin/login?next=${next}`} replace />;
  }

  // Must be admin - all other personas get 403 Forbidden
  if (!isAdmin) {
    return <ForbiddenAccess attemptedPath={location.pathname} />;
  }

  return <>{children}</>;
}

/**
 * Public route that redirects authenticated users to their appropriate dashboard
 * OPTIMIZED: Uses isRoleLoading to prevent premature redirects
 */
export function PublicOnlyRoute({ children }: RouteGuardProps) {
  const { user, isAdmin, isLoading: authLoading, isRoleLoading } = useAuth();
  const { persona, isLoading: personaLoading, isDealerAdmin, isSalesperson, isBuyer } = useUserPersona();

  // Wait for all loading to complete
  if (authLoading || isRoleLoading || (user && personaLoading)) {
    return <AuthLoader />;
  }

  // Not logged in - show public content
  if (!user) {
    return <>{children}</>;
  }

  // Redirect authenticated users to their portal
  if (isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  if (isDealerAdmin) {
    return <Navigate to="/dealer/dashboard" replace />;
  }

  if (isSalesperson) {
    return <Navigate to="/dealer/sales" replace />;
  }

  if (isBuyer) {
    return <Navigate to="/account" replace />;
  }

  // User exists but no persona - allow access (might be onboarding)
  return <>{children}</>;
}
